import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/create/features", label: "Features" },
  { to: "/create/analytics", label: "Analytics" },
  { to: "/create/examples", label: "Examples" },
  { to: "/create/pricing", label: "Pricing" },
  { to: "/create/about", label: "About" },
];

/** Section wrapper: consistent max-width, side padding + optional vertical spacing. */
export function SectionShell({ children, size = "md", className = "", id, dataTestId }) {
  const pad = size === "sm" ? "py-12 sm:py-16" : size === "lg" ? "py-20 sm:py-28" : "py-16 sm:py-24";
  return (
    <section id={id} data-testid={dataTestId} className={`w-full ${pad} ${className}`}>
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8">{children}</div>
    </section>
  );
}

/** Serif editorial heading. Wraps a level-1/2/3 element by props. */
export function EditorialHeading({ as = "h2", size = "lg", children, className = "" }) {
  const Tag = as;
  const sizeCls = size === "xxl"
    ? "text-[38px] leading-[1.05] sm:text-[56px] sm:leading-[1.02] lg:text-[72px]"
    : size === "xl"
    ? "text-[32px] leading-[1.08] sm:text-[44px] lg:text-[54px]"
    : size === "lg"
    ? "text-[28px] leading-[1.1] sm:text-[36px] lg:text-[42px]"
    : "text-[22px] leading-[1.15] sm:text-[26px]";
  return (
    <Tag
      className={`font-[400] tracking-[-0.02em] ${sizeCls} ${className}`}
      style={{ fontFamily: "var(--lm-display)", color: "var(--lm-ink)", fontOpticalSizing: "auto", fontVariationSettings: "'opsz' 96" }}
    >
      {children}
    </Tag>
  );
}

/** Small caps eyebrow label above headings. */
export function Eyebrow({ children, className = "" }) {
  return (
    <p className={`mb-4 text-[11px] font-bold uppercase tracking-[0.24em] ${className}`} style={{ color: "var(--lm-terra)" }}>
      {children}
    </p>
  );
}

/** Body-copy paragraph, larger than default for editorial rhythm. */
export function Lede({ children, className = "" }) {
  return <p className={`text-[17px] leading-[1.6] sm:text-[19px] sm:leading-[1.55] ${className}`} style={{ color: "var(--lm-ink-2)", fontFamily: "var(--lm-sans)" }}>{children}</p>;
}

/** Primary CTA button. Variant: primary | secondary | ghost. */
export function Button({ as = "a", href, to, variant = "primary", size = "md", children, className = "", testid, onClick, ...rest }) {
  const base = "inline-flex items-center justify-center gap-2 font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";
  const sizeCls = size === "lg" ? "px-6 py-4 text-[15px] min-h-[52px]" : size === "sm" ? "px-3.5 py-2 text-[13px] min-h-[36px]" : "px-5 py-3 text-[14px] min-h-[44px]";
  const variants = {
    primary: "text-white rounded-full hover:-translate-y-0.5 active:translate-y-0 shadow-[0_1px_0_rgba(255,255,255,0.15)_inset]",
    secondary: "rounded-full border hover:-translate-y-0.5 active:translate-y-0",
    ghost: "rounded-full hover:bg-[color:var(--lm-cream-2)]",
  };
  const styleByVariant = {
    primary: { background: "var(--lm-ink)", color: "var(--lm-paper)" },
    secondary: { background: "transparent", color: "var(--lm-ink)", borderColor: "var(--lm-hair-strong)" },
    ghost: { color: "var(--lm-ink)" },
  };
  const cls = `${base} ${sizeCls} ${variants[variant]} ${className}`;
  const style = styleByVariant[variant];
  if (to) return <Link to={to} data-testid={testid} className={cls} style={style} onClick={onClick} {...rest}>{children}</Link>;
  if (as === "button") return <button type="button" data-testid={testid} className={cls} style={style} onClick={onClick} {...rest}>{children}</button>;
  return <a href={href} data-testid={testid} className={cls} style={style} onClick={onClick} {...rest}>{children}</a>;
}

export function Pill({ children, tone = "terra", className = "" }) {
  const tones = {
    terra: { background: "var(--lm-terra-light)", color: "var(--lm-terra-dark)" },
    ink: { background: "rgba(26,22,19,0.06)", color: "var(--lm-ink-2)" },
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${className}`} style={tones[tone]}>
      {children}
    </span>
  );
}

/** Hairline card wrapper (thin border, no glow). */
export function Card({ children, className = "", ...rest }) {
  return (
    <div className={`rounded-[var(--lm-r-lg)] border bg-[color:var(--lm-paper)] ${className}`} style={{ borderColor: "var(--lm-hair)" }} {...rest}>
      {children}
    </div>
  );
}

/** Top navigation for marketing pages. Sticky. */
export function MarketingNav() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <header className="sticky top-0 z-40 border-b" style={{ background: "rgba(248,245,238,0.85)", borderColor: "var(--lm-hair)", backdropFilter: "saturate(180%) blur(14px)" }}>
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-4 px-5 sm:px-8">
        <Link to="/create" className="flex items-center gap-2" data-testid="marketing-logo">
          <span className="flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-black" style={{ background: "var(--lm-ink)", color: "var(--lm-paper)", fontFamily: "var(--lm-display)" }}>L</span>
          <span className="text-[17px] font-bold tracking-tight" style={{ color: "var(--lm-ink)" }}>Linkit</span>
        </Link>
        <nav className="ml-6 hidden items-center gap-6 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) => `text-[14px] font-medium transition-colors ${isActive ? "text-[color:var(--lm-terra)]" : "text-[color:var(--lm-ink-2)] hover:text-[color:var(--lm-ink)]"}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto hidden items-center gap-2 md:flex">
          <Button to="/login" variant="ghost" size="sm" testid="nav-login">Log in</Button>
          <Button to="/signup" variant="primary" size="sm" testid="nav-start">Start free</Button>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="ml-auto rounded-full p-2 md:hidden" data-testid="nav-menu-toggle" style={{ color: "var(--lm-ink)" }} aria-label="Menu">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <div className="border-t md:hidden" style={{ borderColor: "var(--lm-hair)", background: "var(--lm-cream)" }}>
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-1 px-5 py-4 sm:px-8">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-m-${l.label.toLowerCase()}`} className="rounded-lg px-3 py-3 text-[15px] font-medium hover:bg-[color:var(--lm-cream-2)]" style={{ color: "var(--lm-ink-2)" }}>
                {l.label}
              </NavLink>
            ))}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button to="/login" variant="secondary" size="md">Log in</Button>
              <Button to="/signup" variant="primary" size="md">Start free</Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

const FOOTER_COLS = [
  {
    title: "Product",
    links: [
      { to: "/create/features", label: "Features" },
      { to: "/create/analytics", label: "Analytics" },
      { to: "/create/examples", label: "Examples" },
      { to: "/create/pricing", label: "Pricing" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/create/about", label: "About" },
      { to: "/create/contact", label: "Contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/create/terms", label: "Terms" },
      { to: "/create/privacy", label: "Privacy" },
      { to: "/create/refund-policy", label: "Refund policy" },
    ],
  },
];

export function MarketingFooter() {
  return (
    <footer className="border-t" style={{ borderColor: "var(--lm-hair)", background: "var(--lm-cream-2)" }}>
      <div className="mx-auto w-full max-w-6xl px-5 py-14 sm:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-black" style={{ background: "var(--lm-ink)", color: "var(--lm-paper)", fontFamily: "var(--lm-display)" }}>L</span>
              <span className="text-[17px] font-bold tracking-tight" style={{ color: "var(--lm-ink)" }}>Linkit</span>
            </div>
            <p className="mt-3 max-w-xs text-[14px] leading-relaxed" style={{ color: "var(--lm-ink-3)" }}>
              A link-in-bio that helps you understand your audience, collect leads, and update everything in minutes.
            </p>
            <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--lm-ink-4)" }}>
              Built by a creator, for creators.
            </p>
          </div>
          {FOOTER_COLS.map((col) => (
            <div key={col.title}>
              <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: "var(--lm-ink-4)" }}>{col.title}</p>
              <ul className="space-y-2">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-[14px] transition-colors hover:text-[color:var(--lm-terra)]" style={{ color: "var(--lm-ink-2)" }}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t pt-6 md:flex-row md:items-center" style={{ borderColor: "var(--lm-hair)" }}>
          <p className="text-[12px]" style={{ color: "var(--lm-ink-4)" }}>© {new Date().getFullYear()} Linkit. All rights reserved.</p>
          <p className="text-[12px]" style={{ color: "var(--lm-ink-4)" }}>Made in India · <Link to="/create/contact" className="hover:text-[color:var(--lm-ink-2)]">Get in touch</Link></p>
        </div>
      </div>
    </footer>
  );
}
